import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faCircleDot } from '@fortawesome/free-solid-svg-icons';
import questions from "../../../questions/numberology/introductiontonumberology.json";

const IntroductionSephiroth = ({ onNextLesson, markLessonComplete }) => {
  // Access the correct lesson data for Sefirot, assuming it is at index 1
  const lessonData = questions[1] || { name: '', content: '', questions: {} };
  const sections = [{ title: lessonData.name, content: lessonData.content }]; // Create sections based on lesson content

  // States
  const [currentSection, setCurrentSection] = useState(0);
  const [currentLevel, setCurrentLevel] = useState('beginner');
  const [selectedOption, setSelectedOption] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [completedSections, setCompletedSections] = useState(
    Array(sections.length).fill(false) // Initialize with the correct length
  );

  useEffect(() => {
    if (completedSections.every((section) => section) && currentLevel === 'intermediate') {
      markLessonComplete();
      onNextLesson();
    }
  }, [completedSections, currentLevel, markLessonComplete, onNextLesson]);

  const handleAnswer = () => {
    const currentQuestion = lessonData.questions[currentLevel];
    if (selectedOption === currentQuestion?.correctAnswer) {
      setFeedback("Correct! Well done.");
      const newCompletedSections = [...completedSections];
      newCompletedSections[currentSection] = true;
      setCompletedSections(newCompletedSections);

      if (currentSection === sections.length - 1) {
        if (currentLevel === 'beginner') {
          setCurrentLevel('intermediate');
          setCurrentSection(0);
          setCompletedSections(Array(sections.length).fill(false));
        } else {
          markLessonComplete();
          onNextLesson();
        }
      } else {
        setTimeout(() => {
          setCurrentSection(currentSection + 1);
          setSelectedOption('');
          setFeedback(null);
        }, 2000);
      }
    } else {
      setFeedback(`Incorrect. The correct answer is: ${currentQuestion?.correctAnswer}.`);
    }
  };

  const handleDotClick = (index) => {
    if (index <= currentSection || completedSections[index - 1]) {
      setCurrentSection(index);
      setSelectedOption('');
      setFeedback(null);
    }
  };

  const transition = { duration: 0.15, ease: 'easeInOut' };

  return (
    <div>
      <Helmet>
        <title>
          {lessonData.name} - {currentLevel.charAt(0).toUpperCase() + currentLevel.slice(1)} Lesson
        </title>
      </Helmet>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentSection}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          <section className="section has-text-centered" id={sections[currentSection]?.id}>
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <h2 className="has-text-primary">{sections[currentSection]?.title}</h2>
                  <div className="has-text-white">{sections[currentSection]?.content}</div>
                </div>
              </div>
            </div>
          </section>

          <section className="section has-text-centered">
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <h2 className="has-text-primary">Test Your Knowledge</h2>
                  <p className="has-text-white">
                    {lessonData.questions[currentLevel]?.question}
                  </p>
                  <div className="is-flex is-align-items-center is-justify-content-center">
                    <div className="select mr-2">
                      <select value={selectedOption} onChange={(e) => setSelectedOption(e.target.value)}>
                        <option value="" disabled>
                          Select an option
                        </option>
                        {lessonData.questions[currentLevel]?.options?.map((option, index) => (
                          <option key={index} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button className="button is-warning" onClick={handleAnswer}>
                      Submit
                    </button>
                  </div>
                  {feedback && <p className="mt-4 has-text-warning">{feedback}</p>}
                </div>
              </div>
            </div>
          </section>

          <section className="section has-text-centered">
            <div className="container content">
              <div className="column">
                <div className="dots mt-4">
                  {sections.map((_, index) => (
                    <FontAwesomeIcon
                      key={index}
                      icon={completedSections[index] ? faCircleDot : faCircle}
                      className={`mr-2 ${
                        index === currentSection
                          ? 'has-text-primary'
                          : completedSections[index]
                          ? 'has-text-warning'
                          : 'has-text-grey'
                      } dot-icon`}
                      onClick={() => handleDotClick(index)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default IntroductionSephiroth;
